// src/MainLayout.js
import React, { useEffect, useState, useCallback, useRef } from 'react';
import io from 'socket.io-client';
import axios from 'axios';
import './i18n';

import Header from './components/Header';
import TradeTab from './components/TradeTab';
import ReferralTab from './components/ReferralTab';
import ProfileTab from './components/ProfileTab';
import Navigation from './components/Navigation';
import InviteModal from './components/InviteModal';
import ResultModal from './components/ResultModal';
import TasksTab from './components/TasksTab';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const socket = io(BACKEND_URL, { autoConnect: true });

const parseServerTime = (timeString) => {
  if (!timeString) return 0;
  return new Date(timeString).getTime();
};

function MainLayout() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('trade');
  const [selectedCrypto, setSelectedCrypto] = useState('BTCUSDT');
  const [balanceType, setBalanceType] = useState('USDT'); // 👈 نوع ارز انتخابی کاربر
  const [user, setUser] = useState(null);

  // Real-time states
  const [currentPrice, setCurrentPrice] = useState('0.00');
  const [currentRound, setCurrentRound] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isFrozen, setIsFrozen] = useState(false);
  
  const [selectedPositions, setSelectedPositions] = useState({
    BTCUSDT: null,
    ETHUSDT: null,
    SOLUSDT: null,
  });

  const [resultModalList, setResultModalList] = useState([]);
  const [showResultModal, setShowResultModal] = useState(false);

  const [activeBets, setActiveBets] = useState({
    BTCUSDT: null,
    ETHUSDT: null,
    SOLUSDT: null,
  });

  const [betAmount, setBetAmount] = useState(10);
  const [loadingBet, setLoadingBet] = useState(false);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [authError, setAuthError] = useState('');

  const [priceHistory, setPriceHistory] = useState({
    BTCUSDT: [],
    ETHUSDT: [],
    SOLUSDT: [],
  });

  const [latestPrices, setLatestPrices] = useState({
    BTCUSDT: '0.00',
    ETHUSDT: '0.00',
    SOLUSDT: '0.00',
  });

  const [activeRoundsCache, setActiveRoundsCache] = useState({
    BTCUSDT: null,
    ETHUSDT: null,
    SOLUSDT: null,
  });

  const activeBetsRef = useRef(activeBets);
  const selectedCryptoRef = useRef(selectedCrypto);
  const userRef = useRef(user);

  useEffect(() => {
    activeBetsRef.current = activeBets;
  }, [activeBets]);

  useEffect(() => {
    selectedCryptoRef.current = selectedCrypto;
  }, [selectedCrypto]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const fetchFullUserProfile = useCallback(async (userId) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/user/${userId}`);
      if (res.data.success) {
        setUser(res.data.user);
      }
    } catch (err) {
      alert(t('errProfileFetch'));
    }
  }, [t]);

  const refreshUserData = useCallback(() => {
    if (userRef.current?.id) {
      fetchFullUserProfile(userRef.current.id);
    }
  }, [fetchFullUserProfile]);

  const loginUser = useCallback(async (id, username, first_name, inviteCodeUsed = '') => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/user/auth`, { id, username, first_name, inviteCodeUsed });
      setUser(res.data.user);
      setShowInviteModal(false);
      fetchFullUserProfile(res.data.user.id || id);
    } catch (err) {
      if (err.response?.status === 403 || err.response?.status === 400) {
        setShowInviteModal(true);
        if (inviteCodeUsed) setAuthError(err.response?.data?.error || 'کد دعوت نامعتبر است');
      }
    }
  }, [fetchFullUserProfile]);

  const fetchActiveRound = useCallback(async (symbol) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/trade/active-round/${symbol}`);
      
      if (res.data && res.data.success && res.data.round) {
        const rawRound = res.data.round;
        const round = {
          ...rawRound,
          total_up: Number(rawRound.total_up || 0),
          total_down: Number(rawRound.total_down || 0),
        };

        setActiveRoundsCache((prev) => ({
          ...prev,
          [symbol]: round,
        }));

        if (symbol === selectedCryptoRef.current) {
          setCurrentRound(round);

          const now = Date.now();
          const freezeAt = parseServerTime(round.freeze_time);
          const isFrozenState = round.status === 'FROZEN' || round.status === 'CLOSED' || now >= freezeAt;
          
          setIsFrozen(isFrozenState);
          setTimeLeft(isFrozenState ? 0 : Math.max(0, Math.floor((freezeAt - now) / 1000)));
        }
      }
    } catch (err) {
      console.warn(`No active round found for ${symbol}`);
    }
  }, []);

  const handleCryptoChange = useCallback((newSymbol) => {
    setSelectedCrypto(newSymbol);
    selectedCryptoRef.current = newSymbol;

    if (latestPrices[newSymbol] && latestPrices[newSymbol] !== '0.00') {
      setCurrentPrice(latestPrices[newSymbol]);
    }

    fetchActiveRound(newSymbol);
  }, [latestPrices, fetchActiveRound]);

  // مدیریت تایمر معکوس
  useEffect(() => {
    if (!currentRound || !currentRound.freeze_time) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const freezeAt = parseServerTime(currentRound.freeze_time);
      
      if (currentRound.status === 'FROZEN' || currentRound.status === 'CLOSED' || now >= freezeAt) {
        setIsFrozen(true);
        setTimeLeft(0);
      } else {
        setIsFrozen(false);
        setTimeLeft(Math.max(0, Math.floor((freezeAt - now) / 1000)));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [currentRound]);

  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (tg) {
      tg.ready();
      tg.expand();
    }

    const tgUser = tg?.initDataUnsafe?.user;
    if (tgUser?.id) {
      loginUser(tgUser.id, tgUser.username, tgUser.first_name);
    } else {
      loginUser(999999, 'preview_user', 'Guest');
    }

    ['BTCUSDT', 'ETHUSDT', 'SOLUSDT'].forEach((sym) => fetchActiveRound(sym));

    const handlePriceUpdate = (data) => {
      const { symbol, price } = data;
      const formattedPrice = parseFloat(price.toFixed(symbol === 'BTCUSDT' ? 2 : 4)).toString();

      setLatestPrices((prev) => ({
        ...prev,
        [symbol]: formattedPrice,
      }));

      if (symbol === selectedCryptoRef.current) {
        setCurrentPrice(formattedPrice);
      }

      setPriceHistory((prev) => {
        const currentList = prev[symbol] || [];
        const updatedList = [...currentList, { time: Date.now(), price: parseFloat(formattedPrice) }];
        return {
          ...prev,
          [symbol]: updatedList.slice(-60),
        };
      });
    };

    const handleRoundStarted = (rawRound) => {
      if (!rawRound) return;

      const round = {
        ...rawRound,
        total_up: Number(rawRound.total_up || 0),
        total_down: Number(rawRound.total_down || 0),
      };

      setActiveRoundsCache((prev) => ({
        ...prev,
        [round.symbol]: round,
      }));

      setSelectedPositions((prev) => ({ ...prev, [round.symbol]: null }));
      setActiveBets((prev) => ({ ...prev, [round.symbol]: null }));

      if (round.symbol === selectedCryptoRef.current) {
        setCurrentRound(round);
        setIsFrozen(false);

        const now = Date.now();
        const freezeAt = parseServerTime(round.freeze_time);
        setTimeLeft(Math.max(0, Math.floor((freezeAt - now) / 1000)));
      }
    };

    const handleRoundFrozen = (data) => {
      const symbol = data.symbol || data.round?.symbol;
      if (!symbol) return;

      setActiveRoundsCache((prev) => {
        const oldRound = prev[symbol];
        if (!oldRound) return prev;
        return {
          ...prev,
          [symbol]: { ...oldRound, status: 'FROZEN', ...(data.id ? data : {}) },
        };
      });

      if (symbol === selectedCryptoRef.current) {
        setIsFrozen(true);
        setTimeLeft(0);
      }
    };

    const handleRoundSettled = async (data) => {
      const roundId = data.roundId || data.id;
      const symbol = data.symbol;

      if (!symbol || !roundId) return;

      const userBetPosition = activeBetsRef.current[symbol];
      const currentUser = userRef.current;

      if (userBetPosition && currentUser?.id) {
        try {
          const res = await axios.get(`${BACKEND_URL}/api/trade/round-result/${roundId}/${currentUser.id}`);

          if (res.data && res.data.success && res.data.hasBet) {
            const result = res.data.data;
            const decimals = result.symbol === 'BTCUSDT' ? 2 : 4;

            // ✅ تبدیل دقیق تایپ‌ها جهت جلوگیری از باگ‌های String/Number/Boolean
            const isWinBool = String(result.isWin) === 'true' || result.isWin === 1 || result.isWin === true;
            const betAmt = parseFloat(result.betAmount || 0);
            const payoutAmt = parseFloat(result.payout || 0);
            
            // محاسبه دقیق netProfit اگر بک‌اند نفرستاده بود یا مقدارش اشتباه بود
            const netProfitAmt = result.netProfit !== undefined 
              ? parseFloat(result.netProfit) 
              : (isWinBool ? payoutAmt - betAmt : -betAmt);

            const newResultItem = {
              roundId: result.roundId,
              isWin: isWinBool, // 👈 مقدار کاملا صریح Boolean
              symbol: result.symbol,
              position: result.userPosition,
              targetPrice: parseFloat(result.openPrice).toFixed(decimals),
              closePrice: parseFloat(result.closePrice).toFixed(decimals),
              betAmount: betAmt,
              reward: payoutAmt,
              netProfit: netProfitAmt
            };

            setResultModalList((prev) => {
              const exists = prev.some((item) => item.roundId === result.roundId);
              if (exists) return prev;
              return [...prev, newResultItem];
            });

            setShowResultModal(true);
          }
        } catch (err) {
          console.error('Error fetching round result:', err);
        }
      }

      setActiveBets((prev) => ({ ...prev, [symbol]: null }));
      setSelectedPositions((prev) => ({ ...prev, [symbol]: null }));

      if (symbol === selectedCryptoRef.current) {
        setIsFrozen(true);
        refreshUserData();
      }

      fetchActiveRound(symbol);
    };

    const handlePoolUpdated = (data) => {
      const { roundId, symbol, total_up, total_down } = data;
      if (!symbol || !roundId) return;

      const numUp = Number(total_up || 0);
      const numDown = Number(total_down || 0);

      setActiveRoundsCache((prev) => {
        const oldRound = prev[symbol];
        if (!oldRound || oldRound.id !== roundId) return prev;

        return {
          ...prev,
          [symbol]: {
            ...oldRound,
            total_up: numUp,
            total_down: numDown,
          },
        };
      });

      if (symbol === selectedCryptoRef.current) {
        setCurrentRound((prev) => {
          if (!prev || prev.id !== roundId) return prev;
          return {
            ...prev,
            total_up: numUp,
            total_down: numDown,
          };
        });
      }
    };
    
    socket.on('price_update', handlePriceUpdate);
    socket.on('round_started', handleRoundStarted);
    socket.on('round_frozen', handleRoundFrozen);
    socket.on('round_settled', handleRoundSettled);
    socket.on('pool_updated', handlePoolUpdated);

    return () => {
      socket.off('price_update', handlePriceUpdate);
      socket.off('round_started', handleRoundStarted);
      socket.off('round_frozen', handleRoundFrozen);
      socket.off('round_settled', handleRoundSettled);
      socket.off('pool_updated', handlePoolUpdated);
    };
  }, [loginUser, fetchActiveRound, refreshUserData]);

  // ثبت شرط‌بندی با در نظر گرفتن نوع موجودی (USDT یا COIN)
  const handleStartDuel = async () => {
    const currentPos = selectedPositions[selectedCrypto];
    const currentBalance = balanceType === 'USDT' 
      ? parseFloat(user?.balance || 0) 
      : parseFloat(user?.coins || 0);

    if (!user) {
      alert(t('errUserNotFound'));
      return;
    }

    if (!currentPos) {
      alert(t('errSelectDirection'));
      return;
    }

    if (betAmount <= 0) {
      alert(t('errInvalidAmount'));
      return;
    }

    if (currentBalance < betAmount) {
      alert(t('errLowBalance'));
      return;
    }

    if (isFrozen) {
      alert(t('errRoundFrozen'));
      return;
    }

    if (!currentRound) {
      alert(t('errNoRound'));
      return;
    }

    if (loadingBet) return;

    setLoadingBet(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/trade/bet`, {
        userId: user.id,
        roundId: currentRound.id,
        position: currentPos,
        amount: parseFloat(betAmount),
        balanceType: balanceType, // 👈 ارسال نوع ارز به بک‌اند
      });

      if (res.data && res.data.success) {
        const addedAmount = parseFloat(betAmount);
        const isUp = currentPos === 'UP';

        // آپدیت بالانس مربوطه در UI
        setUser((prev) => ({
          ...prev,
          balance: balanceType === 'USDT' && res.data.newBalance !== undefined ? res.data.newBalance : prev.balance,
          coins: balanceType === 'COIN' && res.data.newCoins !== undefined ? res.data.newCoins : prev.coins,
        }));

        setActiveBets((prev) => ({
          ...prev,
          [selectedCrypto]: currentPos,
        }));

        setCurrentRound((prev) => {
          if (!prev) return prev;
          const newUp = isUp ? Number(prev.total_up || 0) + addedAmount : Number(prev.total_up || 0);
          const newDown = !isUp ? Number(prev.total_down || 0) + addedAmount : Number(prev.total_down || 0);

          return {
            ...prev,
            total_up: newUp,
            total_down: newDown,
          };
        });

        setActiveRoundsCache((prev) => {
          const oldRound = prev[selectedCrypto];
          if (!oldRound) return prev;

          const newUp = isUp ? Number(oldRound.total_up || 0) + addedAmount : Number(oldRound.total_up || 0);
          const newDown = !isUp ? Number(oldRound.total_down || 0) + addedAmount : Number(oldRound.total_down || 0);

          return {
            ...prev,
            [selectedCrypto]: {
              ...oldRound,
              total_up: newUp,
              total_down: newDown,
            },
          };
        });

      } else {
        alert(t('errBetFailed'));
      }
    } catch (err) {
      console.error('Bet submission error:', err);
      alert(t('errBetFailed'));
    } finally {
      setLoadingBet(false);
    }
  };

  const handleCloseResultModal = () => {
    setShowResultModal(false);
    setResultModalList([]);
  };

  return (
    <div className="min-h-screen bg-[#120F1D] text-white max-w-md mx-auto flex flex-col justify-between pb-20 font-sans select-none" dir="ltr">
      <InviteModal
        isOpen={showInviteModal}
        error={authError}
        onSubmit={(code) => {
          const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user || { id: 999999 };
          loginUser(tgUser.id, tgUser.username, tgUser.first_name, code);
        }}
      />

      <Header user={user} />

      {activeTab === 'trade' && (
        <TradeTab
          user={user}
          selectedCrypto={selectedCrypto}
          setSelectedCrypto={handleCryptoChange}
          balanceType={balanceType}
          setBalanceType={setBalanceType}
          currentPrice={currentPrice}
          currentRound={currentRound}
          isFrozen={isFrozen}
          timeLeft={timeLeft}
          selectedPosition={selectedPositions[selectedCrypto]}
          setSelectedPosition={(pos) =>
            setSelectedPositions((prev) => ({ ...prev, [selectedCrypto]: pos }))
          }
          activeBet={activeBets[selectedCrypto]}
          betAmount={betAmount}
          setBetAmount={setBetAmount}
          loadingBet={loadingBet}
          onStartDuel={handleStartDuel}
          priceHistory={priceHistory[selectedCrypto] || []}
        />
      )}

      {activeTab === 'tasks' && <TasksTab user={user} setUser={setUser} />}

      {activeTab === 'referral' && <ReferralTab user={user} />}

      {activeTab === 'profile' && <ProfileTab user={user} refreshUserData={refreshUserData} />}


      <Navigation activeTab={activeTab} setActiveTab={setActiveTab} />

      <ResultModal
        isOpen={showResultModal}
        onClose={handleCloseResultModal}
        resultsList={resultModalList}
      />
    </div>
  );
}

export default MainLayout;